// Botón "Recientes" + lista de textos ya usados, para los campos libres que el
// usuario repite entre generaciones (ADR-2026-08-12-historial-instrucciones-reusables).
//
// Se monta AL LADO de cada textarea en vez de envolverlo: los campos de
// "Instrucción adicional" viven en cuatro paneles con layouts y estilos
// distintos, y un wrapper común obligaría a uniformarlos.
//
// El componente se queda con lo suyo —cargar el historial, mostrarlo, cerrarse—
// y hacia afuera sólo expone "se eligió este texto". Quien lo usa no sabe que
// existe una tabla atrás.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X } from 'lucide-react';
import { useAgentEnv } from '../agentEnvContext';
import {
  getPromptHistory,
  subscribePromptHistory,
  type PromptHistoryTipo,
} from '../promptHistory';

export interface PromptRecientesButtonProps {
  tipo: PromptHistoryTipo;
  /** Reemplaza el contenido del campo con el texto elegido. */
  onPick: (texto: string) => void;
  /** Override del companyId. Por defecto sale del AgentEnvContext de la v2. */
  companyId?: string | null;
  /** Título de la lista. Cambia según el campo (instrucciones vs fondos). */
  titulo?: string;
  className?: string;
  /**
   * Cómo se dibuja el disparador — ADR-2026-09-08-creador-de-video-recuerda-sus-prompts.
   *
   * - `'texto'` (default): botón gris chico, para vivir DENTRO de un cuadro de texto.
   *   Es el de los cuatro paneles de "Instrucción adicional".
   * - `'pill'`: la pastilla `h-9` de la fila de controles de la caja de prompt, la misma
   *   forma que `Configuración` y `Añadir`. Un botón de texto de 12px al lado de dos
   *   pastillas de 36px se lee como algo de otra jerarquía.
   *
   * Es una prop y no un `className` porque las dos formas difieren en `rounded`, `px` y
   * `text-`: pisar esas utilidades desde afuera depende del orden en que Tailwind las
   * emita en la hoja, que es una carrera que no vale la pena correr.
   */
  variante?: 'texto' | 'pill';
  /**
   * Por qué borde se alinea el popover de desktop con el botón.
   *
   * `'derecha'` (default) es lo que necesita la variante `'texto'`: vive pegada al borde
   * derecho de su panel. La `'pill'` vive a la IZQUIERDA de la caja, y ahí alinear por
   * derecha tiraría la lista 380px hacia afuera de la caja.
   */
  alinear?: 'izquierda' | 'derecha';
}

/** Debe coincidir con las `duration-200` de las transiciones de abajo. */
const DURACION_MS = 200;
/** Aire mínimo contra los bordes de la ventana (desktop). */
const MARGEN_VIEWPORT = 12;
/** Debajo de esto el popover se siente inservible: mejor abrir para el otro lado. */
const ALTO_MINIMO = 220;
const ALTO_MAXIMO = 440;

export const PromptRecientesButton: React.FC<PromptRecientesButtonProps> = ({
  tipo,
  onPick,
  companyId,
  titulo = 'Usadas antes',
  className = '',
  variante = 'texto',
  alinear = 'derecha',
}) => {
  const env = useAgentEnv();
  const idCompany = companyId ?? env.companyId;

  const [items, setItems] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [animado, setAnimado] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  // Posición del popover de desktop. `top` XOR `bottom` según hacia dónde se
  // abra, y `maxH` acotado al espacio real que queda de ese lado.
  const [anchor, setAnchor] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    maxH: number;
  } | null>(null);

  // Carga al montar —no al abrir— para poder ESCONDER el botón cuando no hay
  // nada guardado: un "Recientes" que abre una lista vacía es peor que no
  // tenerlo. La query está memoizada por (empresa, tipo) en `promptHistory`,
  // así que varios botones montados a la vez (mobile + desktop en el mismo
  // árbol) comparten una sola.
  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      void getPromptHistory(idCompany, tipo).then((rows) => {
        if (vivo) setItems(rows);
      });
    };
    cargar();
    // Al generar se invalida la cache: sin esto, la PRIMERA instrucción de una
    // empresa no haría aparecer el botón hasta recargar la app.
    const desuscribir = subscribePromptHistory(cargar);
    return () => {
      vivo = false;
      desuscribir();
    };
  }, [idCompany, tipo]);

  const cerrar = useCallback(() => {
    setAnimado(false);
    const id = setTimeout(() => setOpen(false), DURACION_MS);
    return () => clearTimeout(id);
  }, []);

  const abrir = () => {
    // La posición del popover de desktop se mide en el momento de abrir: el
    // botón vive dentro de paneles que scrollean, así que un rect cacheado
    // quedaría viejo.
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const espacioAbajo = window.innerHeight - rect.bottom - MARGEN_VIEWPORT;
      const espacioArriba = rect.top - MARGEN_VIEWPORT;
      // Estos campos suelen estar al fondo del panel (son el último control
      // antes del Generar), así que abrir siempre hacia abajo dejaba la lista
      // fuera de pantalla. Se abre hacia arriba sólo si abajo no entra un
      // mínimo usable Y arriba hay más lugar.
      const haciaArriba =
        espacioAbajo < ALTO_MINIMO && espacioArriba > espacioAbajo;
      const disponible = haciaArriba ? espacioArriba : espacioAbajo;
      // El alto sale del espacio REAL: con un tope fijo, una lista larga se
      // salía igual de la pantalla. Nunca menos que el mínimo — a partir de
      // ahí scrollea, que para eso el contenedor es `overflow-y-auto`.
      const maxH = Math.max(ALTO_MINIMO, Math.min(ALTO_MAXIMO, disponible));
      // Por qué borde se ata al botón: por el derecho (el default, para los campos
      // pegados al borde de su panel) o por el izquierdo (la pastilla de la caja).
      const left = alinear === 'izquierda' ? rect.left : rect.right;
      setAnchor(
        haciaArriba
          ? {
              bottom: window.innerHeight - rect.top + 8,
              left,
              maxH,
            }
          : { top: rect.bottom + 8, left, maxH },
      );
    }
    setOpen(true);
    // Refresca por si se generó algo desde que se montó (el insert invalida
    // la cache); si no cambió nada, la promesa memoizada vuelve al toque.
    void getPromptHistory(idCompany, tipo).then(setItems);
  };

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setAnimado(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Cerrar con Escape, como el resto de los overlays de la v2.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, cerrar]);

  if (items.length === 0) return null;

  const elegir = (texto: string) => {
    onPick(texto);
    cerrar();
  };

  // Cada texto va como card. Son párrafos largos —descripciones de fondo de
  // varias líneas—: en una lista con separadores se leían como un bloque
  // corrido y costaba ver dónde termina uno y empieza el otro.
  const lista = (
    <ul className="flex flex-col gap-2.5 px-4 pb-4">
      {items.map((texto, i) => (
        <li key={`${i}-${texto.slice(0, 24)}`}>
          <button
            type="button"
            onClick={() => elegir(texto)}
            className="w-full text-left px-4 py-3.5 rounded-2xl bg-neutral-50 border border-neutral-200 hover:border-brand-primary/40 hover:bg-white active:bg-neutral-100 transition-colors"
          >
            {/* Tope de 4 líneas; el resto lo corta el navegador con "…". */}
            <span className="block text-[13.5px] leading-snug text-brand-black/85 line-clamp-4">
              {texto}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={abrir}
        // `texto`: gris apagado a propósito — vive DENTRO del cuadro de texto, y en azul
        // competía con el CTA de la pantalla ("Utilizar" / "Generar"). `flex w-fit` (y no
        // `inline-flex`) para que el consumidor pueda alinearlo con `self-end`, `ml-auto`
        // o posicionarlo absoluto.
        // `pill`: la forma exacta de `ConfiguracionMenu` — redonda de sólo ícono en
        // mobile, con rótulo en desktop.
        className={
          variante === 'pill'
            ? `inline-flex items-center justify-center gap-1.5 h-9 w-9 md:w-auto md:px-3.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] shrink-0 transition-colors ${className}`
            : `flex w-fit items-center gap-1.5 py-1 px-2 rounded-lg text-[12px] font-medium text-[#3C3C43]/80 hover:text-brand-black hover:bg-black/[0.05] active:bg-black/[0.08] transition-colors ${className}`
        }
      >
        {variante === 'pill' ? (
          <>
            <History className="w-4 h-4 shrink-0" strokeWidth={2.25} />
            <span className="hidden md:inline text-[13.5px] font-medium whitespace-nowrap">
              Recientes
            </span>
          </>
        ) : (
          <>
            <History className="w-3.5 h-3.5" />
            Recientes
          </>
        )}
      </button>

      {/* Portaleado a document.body: estos campos viven dentro de paneles
          `fixed` con overflow propio — inline, la lista quedaría recortada por
          el scroll del panel o por debajo de la sidebar. */}
      {open &&
        createPortal(
          <>
            <div
              className={`fixed inset-0 z-[240] bg-black/30 transition-opacity duration-200 ${
                animado ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={cerrar}
            />

            {/* Mobile: bottom sheet que crece con el contenido hasta casi el
                borde de arriba. Con `max-h-[60vh]` entraban dos textos y medio;
                con `top` fijo, en cambio, dos ítems dejaban media pantalla
                blanca. De ahí en más scrollea la lista. */}
            <div
              className={`sm:hidden fixed inset-x-0 bottom-0 z-[241] max-h-[calc(100dvh-24px)] bg-white rounded-t-[20px] shadow-[0_-8px_40px_rgba(0,0,0,0.14)] flex flex-col transition-transform duration-200 ease-out ${
                animado ? 'translate-y-0' : 'translate-y-full'
              }`}
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-2">
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  {titulo}
                </h3>
                <button
                  type="button"
                  onClick={cerrar}
                  aria-label="Cerrar"
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto pb-2">{lista}</div>
            </div>

            {/* Desktop: popover anclado al botón. Con `alinear="derecha"` (el default)
                el `translateX(-100%)` lo pega por la derecha al botón, que suele estar
                contra el borde del panel; con `"izquierda"` no se traslada y crece hacia
                la derecha desde el botón. Se abre hacia arriba o hacia abajo según el
                lugar que haya (`anchor`), y el alto sale de ahí, no de un tope fijo. */}
            <div
              className={`hidden sm:flex fixed z-[241] w-[380px] max-w-[92vw] bg-white rounded-[14px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] flex-col overflow-hidden transition-opacity duration-200 ${
                animado ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                top: anchor?.top,
                bottom: anchor?.bottom,
                left: anchor?.left ?? 0,
                maxHeight: anchor?.maxH ?? ALTO_MAXIMO,
                transform:
                  alinear === 'izquierda' ? undefined : 'translateX(-100%)',
              }}
            >
              <div className="shrink-0 px-4 pt-3 pb-2 text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                {titulo}
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto pb-1">{lista}</div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default PromptRecientesButton;
